import React from "react";
import {
  Container,
  Box,
  Typography,
  Stack,
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import MiniDrawer from "../../components/MiniDrawer.js";
import useDashboardData from "./DashboardData.js";
import useDashboardDataVitals from "./DashboardDataVitals.js";
import ClientDemographicsCard from "./ClientDemographicsCard.js";
import RiskDistributionInfo from "./RiskDistributionInfo.js";
import useAlert from '../../alert/useAlert.js';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const Dashboard = () => {
  const isCorporateVitalsMode = false;

  const normalData = useDashboardData({ enabled: !isCorporateVitalsMode });
  const corporateVitalsData = useDashboardDataVitals({ enabled: isCorporateVitalsMode });

  const {
    timeRange,
    setTimeRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    loading,
    matchedMindvaultIds,
    activeMindvaultIds,
    scanCount,
    chatCount,
    appointmentCount,
    scanSeries,
    riskCounts,
    consultationThemes,
    demographics,
    isSingleDaySeries,
    teamMembersData,
    selectedCaseOwnerFilter,
    setSelectedCaseOwnerFilter,
    currentUserRole,
    userDetails,
  } = isCorporateVitalsMode ? corporateVitalsData : normalData;

  const { setAlert } = useAlert();
  const isAdmin = currentUserRole === 'Admin';

  const handleCaseOwnerChange = (e) => {
    const value = e.target.value;
    if (!isAdmin && value === 'ALL') {
      setAlert('Only team admins can view all client records.', 'warning');
      return;
    }
    if (!isAdmin && value !== userDetails?.id) {
      setAlert('Only team admins can view client records of other team members.', 'warning');
      return;
    }
    setSelectedCaseOwnerFilter(value);
  };

  const [visibleLines, setVisibleLines] = React.useState({ scans: true, chats: true });

  const themeColors = {
    teal: '#10B981',
    blue: '#3B82F6',
    purple: '#8B5CF6',
    cyan: '#06B6D4',
    orange: '#F59E0B',
    red: '#EF4444',
  };

  const handleLegendClick = (e) => {
    const { dataKey } = e;
    setVisibleLines((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  return (
    <Container disableGutters maxWidth={false}>
      <Grid container sx={{ overflow: 'auto', minHeight: '100vh', minWidth: '100vw' }}>
        <Grid size={{ xs: 1, md: 0 }}>
          <MiniDrawer />
        </Grid>
        <Grid size="grow">
          <Grid container spacing={5} sx={{ backgroundColor: '#F8F9FA', display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            <Grid size={12}>
              <div style={{ padding: '24px 10vw', paddingBottom: 40 }}>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Box data-tour="dashboard-overview">
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Dashboard</Typography>
                  </Box>
                </Stack>
                <>
                  {/* Filters */}
                  <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <FormControl size="small">
                      <Select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        sx={{ borderRadius: 2, minWidth: 160 }}
                      >
                        <MenuItem value="week">This Week</MenuItem>
                        <MenuItem value="month">This Month</MenuItem>
                        <MenuItem value="quarter">This Quarter</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl size="small">
                      <Select
                        value={selectedCaseOwnerFilter || 'ALL'}
                        onChange={handleCaseOwnerChange}
                        sx={{ borderRadius: 2, minWidth: 200 }}
                        displayEmpty
                      >
                        <MenuItem
                          value="ALL"
                          sx={!isAdmin ? { color: 'text.disabled', opacity: 0.5 } : {}}
                        >
                          All Members
                        </MenuItem>
                        {(teamMembersData || []).map((member) => {
                          const restricted = !isAdmin && member.id !== userDetails?.id;
                          return (
                            <MenuItem
                              key={member.id}
                              value={member.id}
                              sx={restricted ? { color: 'text.disabled', opacity: 0.5 } : {}}
                            >
                              {member.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>

                    {timeRange === 'custom' && (
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          type="date"
                          value={customFrom}
                          onChange={(e) => setCustomFrom(e.target.value)}
                        />
                        <TextField
                          size="small"
                          type="date"
                          value={customTo}
                          onChange={(e) => setCustomTo(e.target.value)}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setCustomFrom('');
                            setCustomTo('');
                          }}
                        >
                          Reset
                        </Button>
                      </Stack>
                    )}
                  </Stack>

                  {/* KPI Cards + Charts with loading overlay */}
                  <Box sx={{ position: 'relative' }}>
                    {loading && (
                      <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 10,
                        backgroundColor: 'rgba(248,249,250,0.75)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 3,
                      }}>
                        <CircularProgress size={48} thickness={4} />
                      </Box>
                    )}
                  <Grid data-tour="dashboard-metrics" container spacing={3} sx={{ mb: 3 }}>
                    {[
                      { title: 'Total Clients', value: matchedMindvaultIds.length, sub: `${activeMindvaultIds.length} active this ${timeRange === 'week' ? 'week' : timeRange === 'month' ? 'month' : timeRange === 'quarter' ? 'quarter' : 'range'}` },
                      { title: 'Vitals Scans', value: scanCount, sub: 'This period' },
                      { title: 'Chat Sessions', value: chatCount, sub: 'This period' },
                      ...(!isCorporateVitalsMode
                        ? [{ title: 'Therapy Sessions Completed', value: appointmentCount, sub: 'This period' }]
                        : []),
                    ].map((card, idx) => (
                      <Grid key={idx} size={{ xs: 12, sm: 6, md: 3 }}>
                        <Paper sx={{ p: 3, borderRadius: 3 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {card.value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {card.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {card.sub}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Charts Row */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* First in the 2x2: who the caseload is, before anything about
                        what it did this week. Unlike the three panels after it,
                        this one ignores the time-range filter — a caseload's
                        demographics are who the practice sees now, not who it saw
                        this week. The ⓘ on the card says so. */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <ClientDemographicsCard demographics={demographics} />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3, borderRadius: 3, height: { xs: 'auto', md: 380 }, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              No. of Vitals Scans & Chat Sessions
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                              Last period trend
                          </Typography>
                          <Box sx={{ flex: { xs: 'none', md: 1 }, height: { xs: 300, md: 'auto' }, mt: 2 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={scanSeries}>
                              <CartesianGrid strokeDasharray="3 3" />
                              {isSingleDaySeries ? (
                                <XAxis
                                  type="number"
                                  dataKey="minute"
                                  domain={[0, 1440]}
                                  ticks={Array.from({ length: 25 }, (_, i) => i * 60)}
                                  tickFormatter={(v) => `${String(Math.floor(v / 60)).padStart(2, '0')}:00`}
                                />
                              ) : (
                                <XAxis dataKey="day" />
                              )}
                              <YAxis allowDecimals={false} interval={0} />
                              <Tooltip
                                labelFormatter={(label, payload) => {
                                  if (isSingleDaySeries) {
                                    return payload?.[0]?.payload?.timeLabel || '';
                                  }
                                  return label;
                                }}
                              />
                              <Legend onClick={handleLegendClick} style={{ cursor: 'pointer' }} />
                              <Line
                                type="monotone"
                                dataKey="scans"
                                name="Scans"
                                stroke={themeColors.teal}
                                dot={{ r: 3 }}
                                hide={!visibleLines.scans}
                                isAnimationActive={false}
                              />
                              <Line
                                type="monotone"
                                dataKey="chats"
                                name="Chats"
                                stroke={themeColors.blue}
                                dot={{ r: 3 }}
                                hide={!visibleLines.chats}
                                isAnimationActive={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3, borderRadius: 3, height: { xs: 'auto', md: 380 }, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Consultation Themes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Distribution by topic
                        </Typography>
                        {consultationThemes.length === 0 ? (
                          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary">No theme data available for this period</Typography>
                          </Box>
                        ) : (
                          <Grid container spacing={2} sx={{ mt: 1, flex: 1 }}>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Box sx={{ height: 240 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie data={consultationThemes} dataKey="value" innerRadius={55} outerRadius={85} isAnimationActive={false}>
                                      {consultationThemes.map((entry, idx) => (
                                        <Cell key={`cell-${idx}`} fill={entry.color} />
                                      ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `${value}%`} />
                                  </PieChart>
                                </ResponsiveContainer>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                              <Box sx={{ overflowY: 'auto', maxHeight: 240, pr: 1 }}>
                                <Stack spacing={1}>
                                  {consultationThemes.map((t, idx) => (
                                    <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: t.color, flexShrink: 0 }} />
                                        <Typography variant="body2">{t.name}</Typography>
                                      </Stack>
                                      <Typography variant="body2" sx={{ fontWeight: 600, ml: 1, flexShrink: 0 }}>{t.value}%</Typography>
                                    </Stack>
                                  ))}
                                </Stack>
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3, borderRadius: 3, height: { xs: 'auto', md: 380 }, display: 'flex', flexDirection: 'column' }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Client Risk Distribution
                            </Typography>
                            <RiskDistributionInfo />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                              Current risk assessment
                          </Typography>
                          <Grid container spacing={2} sx={{ mt: 2, flex: 1 }}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <ResponsiveContainer width="100%" height={220}>
                              <PieChart>
                                <Pie
                                  data={[
                                    { name: 'Normal', value: riskCounts.NORMAL ?? 0 },
                                    { name: 'Low', value: riskCounts.LOW },
                                    { name: 'Medium', value: riskCounts.MEDIUM },
                                    { name: 'High', value: riskCounts.HIGH },
                                  ]}
                                  dataKey="value"
                                  innerRadius={55}
                                  outerRadius={90}
                                  isAnimationActive={false}
                                >
                                  <Cell fill="#10B981" />
                                  <Cell fill="#FBBF24" />
                                  <Cell fill="#F59E0B" />
                                  <Cell fill="#EF4444" />
                                </Pie>
                              </PieChart>
                            </ResponsiveContainer>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={1}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10B981', flexShrink: 0 }} />
                                  <Typography variant="body2">Normal</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{riskCounts.NORMAL ?? 0}</Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#FBBF24', flexShrink: 0 }} />
                                  <Typography variant="body2">Low Risk</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{riskCounts.LOW}</Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#F59E0B', flexShrink: 0 }} />
                                  <Typography variant="body2">Medium Risk</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{riskCounts.MEDIUM}</Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#EF4444', flexShrink: 0 }} />
                                  <Typography variant="body2">High Risk</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{riskCounts.HIGH}</Typography>
                              </Stack>
                              <Divider sx={{ my: 1 }} />
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>Total</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {(riskCounts.NORMAL ?? 0) + riskCounts.LOW + riskCounts.MEDIUM + riskCounts.HIGH}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3, borderRadius: 3, height: 380, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Consultation Themes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                          Distribution by topic
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 1, flex: 1 }}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ height: 240 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie data={consultationThemes} dataKey="value" innerRadius={55} outerRadius={85}>
                                    {consultationThemes.map((entry, idx) => (
                                      <Cell key={`cell-${idx}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                </PieChart>
                              </ResponsiveContainer>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={1}>
                              {consultationThemes.map((t, idx) => (
                                <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: t.color }} />
                                    <Typography variant="body2">{t.name}</Typography>
                                  </Stack>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.value}%</Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid> */}
                  </Grid>
                  </Box>
                </>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
